import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Icon from "../../atoms/icon";
import { get } from "lodash";
import styles from "./styles.m.css";
import DigitalEbookStoryCard from "../../atoms/digital-ebook-story-card";
import ResponsiveImageWithFallback from "../../atoms/responsive-image-with-fallback";
import { getStoryData, generateImageSources } from "../../utils/utils";
import { Link } from "@quintype/components";
import { isPremium } from "../../../../isomorphic/data/story";

const SliderEbookCard = ({ collection, accentColor }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [showEbookOfferPopup, setShowEbookOfferPopup] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);
  const [isClosedManually, setIsClosedManually] = React.useState(false);
  const lastScrollY = useRef(0);
  const [userType, setUserType] = React.useState("F");

  useEffect(() => {
    if (global && global.userPromise) {
      global.userPromise.catch(() => ({})).then(user => {
        if (user && user.userType) {
          setUserType(user.userType);
        }
      });
    }
  }, []);

  useEffect(
    () => {
      const handleScroll = () => {
        if (isClosedManually || userType === "P" || userType === "T" || userType === "R") return;
        const currentScrollY = window.scrollY;
        const scrollThreshold = 0.4 * (global.window ? window.innerHeight : 400);

        if (currentScrollY > scrollThreshold) {
          setShowEbookOfferPopup(true);
          setIsVisible(true);
        } else if (currentScrollY < scrollThreshold * 0.8) {
          setIsVisible(false);
        }
        lastScrollY.current = currentScrollY;
      };

      if (global.window) {
        window.addEventListener("scroll", handleScroll, { passive: true });
      }
      return () => {
        if (global.window) {
          window.removeEventListener("scroll", handleScroll);
        }
      };
    },
    [isClosedManually, userType]
  );

  if (!collection || !collection.items || collection.items.length === 0) {
    return null;
  }

  const topStory = collection.items[0].story;
  const topStoryData = getStoryData(topStory, false);
  const topAmazonUrl = get(topStory, ["metadata", "story-attributes", "amazonurl", 0]);

  const description = get(topStory, ["cards", 0, "story-elements", 0, "text"], "E book").replace(/<[^>]*>/g, "");
  const maxLength = 1000;
  const shortDescription = description.length > maxLength ? description.slice(0, maxLength) + "..." : description;

  const domainName = global && global.location && global.location.origin;

  const gridItems = collection.items.slice(1).map(({ story }) => (
    <DigitalEbookStoryCard
      key={story.id}
      story={story}
      className={styles.card}
      onClickEbookOffer={() => {
        if (userType !== "P" && userType !== "T" && userType !== "R") {
          setShowEbookOfferPopup(true);
          setIsVisible(true);
        }
      }}
    />
  ));

  return (
    <div className={styles.base}>
      <div className={`container ${styles["carousel-wrapper"]}`}>
        <div className={styles["collection-header"]}>
          <div className={styles["top-card-custom"]}>
            <ResponsiveImageWithFallback
              className={styles["featured-image"]}
              slug={topStoryData.imageS3Key}
              metadata={topStoryData.imageMetadata}
              isPremium={isPremium(topStory)}
              alt={topStoryData.imageCaption}
              imgParams={{ auto: ["format", "compress"] }}
              sources={generateImageSources(
                { aspectRatio: [3, 4], screenWidthCoverage: 0 },
                { aspectRatio: [3, 4], screenWidthCoverage: 0.25 }
              )}
              story={topStory}
            />
            <div className={styles["header-cta-row"]}>
              {userType === "P" || userType === "T" || userType === "R" ? (
                <Link className={styles["header-cta"]} href={"https://onelink.to/cm4vtj"}>
                  Read in app
                </Link>
              ) : (
                <Link className={styles["header-cta"]} href={topAmazonUrl}>
                  Buy e-book
                </Link>
              )}
            </div>
          </div>
          <div className={styles["header-content"]}>
            <h1 className={styles["collection-title"]}>{topStory.headline}</h1>
            <div className={styles["collection-description"]}>
              {expanded ? description : shortDescription}
              {description.length > maxLength && (
                <button className={styles["readmore-button"]} onClick={() => setExpanded(!expanded)}>
                  {expanded ? "Read Less" : "Read More"}
                  <Icon type={expanded ? "angle-up" : "angle-down"} className={styles["readmore-icon"]} />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className={styles["slider-wrapper"]}>
          <h2 className={styles["grid-heading"]}>Other Editions</h2>
          <div className={styles["grid-container"]}>{gridItems}</div>
        </div>
      </div>
      {showEbookOfferPopup && (
        <div
          className={`${styles["ebook-offer-popup-overlay"]} ${isVisible ? styles["is-visible"] : styles["is-hidden"]}`}
        >
          <div className={styles["ebook-offer-popup-content"]}>
            {/* Dark header band: logo + close */}
            <div className={styles["ebook-offer-popup-header"]}>
              <div className={styles["ebook-offer-popup-logo"]}>
                <img src="https://images.assettype.com/vikatan/2026-03-21/kmlm13n9/vikatan-icon.png" alt="Vikatan" />
              </div>
              <button
                className={styles["ebook-offer-popup-close"]}
                onClick={() => {
                  setIsVisible(false);
                  setIsClosedManually(true);
                  setTimeout(() => setShowEbookOfferPopup(false), 600);
                }}
              >
                <Icon type="close" />
              </button>
            </div>

            {/* Body */}
            <div className={styles["ebook-offer-popup-body"]}>
              <span className={styles["ebook-offer-badge"]}>
                <span className={styles["ebook-offer-badge-dot"]} />
                Limited Time Offer
              </span>

              <div className={styles["ebook-offer-popup-description"]}>
                <p>அன்பு வாசகரே,</p>
                <p>₹10,000 மதிப்பிலான விகடன் டிஜிட்டல் புத்தகங்களை தங்குதடையின்றி வாசிக்க இன்றே சந்தாதாரர் ஆகுங்கள்.</p>
              </div>

              <ul className={styles["ebook-offer-features"]}>
                <li>விகடன் டிஜிட்டல் சந்தா</li>
                <li>டிஜிட்டல் புக்ஸ் அன்லிமிடெட்</li>
                <li>விகடன் ப்ளே ஆக்சஸ்</li>
              </ul>

              <div className={styles["ebook-offer-popup-cta"]}>
                <a
                  href="https://www.vikatan.com/store?utm_source=direct_web&utm_medium=subscribe_button_mob&utm_campaign=subscribe_button_digital_ebook_offer"
                  className={styles["subscribe-btn"]}
                >
                  GET OFFER
                </a>
                <a href={`${domainName}/user/login`} className={styles["login-btn"]}>
                  Already a subscriber? Log in
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

SliderEbookCard.propTypes = {
  accentColor: PropTypes.string,
  collection: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    items: PropTypes.array
  }),
  numberOfCardsInViewDesktop: PropTypes.number,
  numberOfCardsInViewTablet: PropTypes.number,
  numberOfCardsInViewMobile: PropTypes.number
};

export default SliderEbookCard;
